import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Chevron from 'assets/icons/Chevron'
import { CloseIcon } from 'assets/icons/CloseIcon'
import useQueryPagination from 'hooks/useQueryPagination'
import {
  blockRoom,
  checkOut,
  clearAccommodationInfo,
  getBedsHistory,
  getCamps,
  markBedClean,
  markRoomClean,
} from 'features/accommodation/accommodationSlice'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import { AppDispatch, RootState } from 'store'
import { IGetAllCampsSingleResponse } from 'types/api/accommodationApi.interface'
import { IAccommodationOccupant, IComputedCamp } from 'types/accommodation.interface'
import { NoData } from 'components/NoData'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { tokens } from 'styling/tokens'
import { formatDateForDisplay } from 'utils/helpers'
import { AddAccommodationModal } from './AddAccommodationModal'
import { LocationIcon } from '../icons/LocationIcon'
import { NextIcon } from '../icons/NextIcon'
import { BED_BLOCKED, BED_DIRTY, buildOccupantMap, computeCamps } from '../accommodationStats'
import {
  SectionLabel,
  SectionCount,
  CampList,
  CampCard,
  CampHeader,
  CampIdentity,
  CampName,
  CampMeta,
  CampKpis,
  CampKpi,
  CampKpiValue,
  CampKpiLabel,
  ChevronBox,
  CampCollapse,
  CampCollapseInner,
} from './AccommodationCamp.styled'
import {
  HeaderActions,
  IconBtn,
  RoomList,
  RoomGroup,
  RoomHeader,
  RoomTitle,
  RoomName,
  RoomContext,
  RoomCount,
  BedChips,
  BedChipDot,
  BedChipLabel,
  BedChipState,
  RoomEmpty,
  BedChipButton,
  BedChipTop,
  BedChipOccupant,
  OverdueDot,
  DrawerScrim,
  DrawerPanel,
  DrawerHeader,
  DrawerTitle,
  DrawerBedLabel,
  DrawerLocation,
  DrawerClose,
  DrawerBody,
  DrawerField,
  DrawerFieldLabel,
  DrawerFieldValue,
  DrawerFieldRow,
  DrawerActions,
} from './BedManagement.styled'
import { ExtendStayModal } from './ExtendStayModal'

const stateInfo = (
  state: number,
): { label: string; color: string; pill: 'success' | 'info' | 'warning' | 'danger' | 'neutral' } => {
  switch (state) {
    case 1:
      return { label: 'Available', color: tokens.status.success.fg, pill: 'success' }
    case 2:
      return { label: 'Occupied', color: tokens.status.info.fg, pill: 'info' }
    case 3:
      return { label: 'Dirty', color: tokens.status.warning.fg, pill: 'warning' }
    case 4:
      return { label: 'Blocked', color: tokens.status.danger.fg, pill: 'danger' }
    default:
      return { label: 'Unknown', color: tokens.border.strong, pill: 'neutral' }
  }
}

const genderLabel = (g: number) => (g === 1 ? 'Male' : g === 2 ? 'Female' : g === 3 ? 'Both' : 'Any')
const tierLabel = (t: number) =>
  t === 1 ? 'VIP' : t === 2 ? 'Senior' : t === 3 ? 'Junior' : t === 4 ? 'Contractor' : 'Any'

const PencilIcon = () => (
  <svg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M11.4 2.1l2.5 2.5L5.6 12.9l-3 .5.5-3 8.3-8.3z' stroke='currentColor' strokeWidth='1.2' strokeLinejoin='round' />
  </svg>
)

interface SelectedBed {
  bedId: number
  label: string
  state: number
  roomId: number
  outOfService: boolean
  roomName: string
  blockName: string
  campName: string
  occupant: IAccommodationOccupant | null
}

const CampGroup = ({
  camp,
  active,
  onToggle,
  onEdit,
  onSelectBed,
}: {
  camp: IComputedCamp
  active: boolean
  onToggle: () => void
  onEdit: () => void
  onSelectBed: (bed: SelectedBed) => void
}) => {
  const rooms = camp.blocks.flatMap((block) => block.rooms)

  return (
    <CampCard $active={active}>
      <CampHeader onClick={onToggle}>
        <CampIdentity>
          <CampName>{camp.name}</CampName>
          <CampMeta>
            {camp.blocksCount} blocks · {rooms.length} rooms · {camp.counts.total} beds
          </CampMeta>
        </CampIdentity>
        <CampKpis>
          <CampKpi>
            <CampKpiValue $color={tokens.status.info.fg}>{camp.counts.occupancy}%</CampKpiValue>
            <CampKpiLabel>Camp occupancy</CampKpiLabel>
          </CampKpi>
          <CampKpi>
            <CampKpiValue $color={tokens.status.success.fg}>{camp.counts.available}</CampKpiValue>
            <CampKpiLabel>Available</CampKpiLabel>
          </CampKpi>
          <CampKpi>
            <CampKpiValue $color={camp.counts.dirty ? tokens.status.warning.fg : tokens.text.secondary}>
              {camp.counts.dirty}
            </CampKpiValue>
            <CampKpiLabel>Cleaning</CampKpiLabel>
          </CampKpi>
          <CampKpi>
            <CampKpiValue $color={camp.counts.blocked ? tokens.status.danger.fg : tokens.text.secondary}>
              {camp.counts.blocked}
            </CampKpiValue>
            <CampKpiLabel>Blocked</CampKpiLabel>
          </CampKpi>
          <HeaderActions>
            <IconBtn
              type='button'
              aria-label='Edit camp'
              onClick={(e) => {
                e.stopPropagation()
                onEdit()
              }}
            >
              <PencilIcon />
            </IconBtn>
            <ChevronBox>
              <Chevron open={active} />
            </ChevronBox>
          </HeaderActions>
        </CampKpis>
      </CampHeader>

      <CampCollapse $open={active}>
        <CampCollapseInner>
          <RoomList>
            {rooms.length ? (
              rooms.map((room) => (
                <RoomGroup key={room.id}>
                  <RoomHeader>
                    <RoomTitle>
                      <RoomName>{room.name}</RoomName>
                      <RoomContext>
                        {room.blockName} · {genderLabel(room.gender)} · {tierLabel(room.tier)}
                      </RoomContext>
                    </RoomTitle>
                    {room.outOfService ? (
                      <StatusPill status='danger' dot>
                        Room blocked
                      </StatusPill>
                    ) : (
                      <RoomCount>
                        {room.counts.occupied}/{room.counts.total} occupied
                      </RoomCount>
                    )}
                  </RoomHeader>
                  {room.beds.length ? (
                    <BedChips>
                      {room.beds.map((bed) => {
                        const info = stateInfo(bed.effectiveState)
                        return (
                          <BedChipButton
                            type='button'
                            key={bed.id}
                            $accent={info.color}
                            $clickable
                            onClick={() =>
                              onSelectBed({
                                bedId: bed.id,
                                label: bed.label,
                                state: bed.effectiveState,
                                roomId: room.id,
                                outOfService: room.outOfService,
                                roomName: room.name,
                                blockName: room.blockName,
                                campName: room.campName,
                                occupant: bed.occupant,
                              })
                            }
                          >
                            <BedChipTop>
                              <BedChipDot $color={info.color} />
                              <BedChipLabel>{bed.label}</BedChipLabel>
                              <BedChipState>{info.label}</BedChipState>
                            </BedChipTop>
                            {bed.occupant && (
                              <BedChipOccupant $overdue={bed.occupant.overdue}>
                                {bed.occupant.overdue && <OverdueDot />}
                                {bed.occupant.name}
                              </BedChipOccupant>
                            )}
                          </BedChipButton>
                        )
                      })}
                    </BedChips>
                  ) : (
                    <RoomEmpty>No beds in this room</RoomEmpty>
                  )}
                </RoomGroup>
              ))
            ) : (
              <RoomEmpty>No rooms configured for this camp</RoomEmpty>
            )}
          </RoomList>
        </CampCollapseInner>
      </CampCollapse>
    </CampCard>
  )
}

const BedDrawer = ({
  bed,
  onClose,
  onCheckOut,
  onExtend,
  onMarkClean,
  onReturnToService,
  onBlock,
}: {
  bed: SelectedBed
  onClose: () => void
  onCheckOut: (assignmentId: number) => void
  onExtend: (assignmentId: number) => void
  onMarkClean: (bedId: number) => void
  onReturnToService: (bed: SelectedBed) => void
  onBlock: (roomId: number) => void
}) => {
  const info = stateInfo(bed.state)
  const occupant = bed.occupant

  return (
    <>
      <DrawerScrim onClick={onClose} />
      <DrawerPanel>
        <DrawerHeader>
          <DrawerTitle>
            <DrawerBedLabel>{bed.label}</DrawerBedLabel>
            <DrawerLocation>
              <LocationIcon />
              {bed.campName} <NextIcon /> {bed.blockName} <NextIcon /> {bed.roomName}
            </DrawerLocation>
            <div>
              <StatusPill status={info.pill} dot>
                {info.label}
              </StatusPill>
            </div>
          </DrawerTitle>
          <DrawerClose type='button' aria-label='Close' onClick={onClose}>
            <CloseIcon />
          </DrawerClose>
        </DrawerHeader>

        <DrawerBody>
          {occupant ? (
            <>
              <DrawerField>
                <DrawerFieldLabel>Occupant</DrawerFieldLabel>
                <DrawerFieldValue>{occupant.name}</DrawerFieldValue>
              </DrawerField>
              <DrawerFieldRow>
                <DrawerField>
                  <DrawerFieldLabel>Check-in</DrawerFieldLabel>
                  <DrawerFieldValue>{formatDateForDisplay(occupant.checkIn)}</DrawerFieldValue>
                </DrawerField>
                <DrawerField>
                  <DrawerFieldLabel>Expected check-out</DrawerFieldLabel>
                  <DrawerFieldValue $danger={occupant.overdue}>
                    {formatDateForDisplay(occupant.expectedCheckOut)}
                    {occupant.overdue ? ' · Overdue' : ''}
                  </DrawerFieldValue>
                </DrawerField>
              </DrawerFieldRow>
              <DrawerField>
                <DrawerFieldLabel>Person type</DrawerFieldLabel>
                <DrawerFieldValue>{occupant.personType === 1 ? 'Employee' : 'Guest'}</DrawerFieldValue>
              </DrawerField>
            </>
          ) : (
            <DrawerField>
              <DrawerFieldLabel>Status</DrawerFieldLabel>
              <DrawerFieldValue>
                {bed.state === BED_DIRTY
                  ? 'This bed is dirty and awaiting cleaning.'
                  : bed.state === BED_BLOCKED
                    ? bed.outOfService
                      ? 'This room is blocked. All its beds are out of service.'
                      : 'This bed is blocked and unavailable.'
                    : 'This bed is available.'}
              </DrawerFieldValue>
            </DrawerField>
          )}
        </DrawerBody>

        <DrawerActions>
          {occupant && (
            <>
              <Button variant='success' onClick={() => onCheckOut(occupant.assignmentId)}>
                Check out
              </Button>
              <Button variant='secondary' onClick={() => onExtend(occupant.assignmentId)}>
                Extend stay
              </Button>
            </>
          )}
          {!occupant && bed.state === BED_DIRTY && (
            <Button variant='primary' onClick={() => onMarkClean(bed.bedId)}>
              Mark clean
            </Button>
          )}
          {!occupant && bed.state === BED_BLOCKED && (
            <Button variant='primary' onClick={() => onReturnToService(bed)}>
              Return to service
            </Button>
          )}
          {!occupant && bed.state !== BED_BLOCKED && (
            <Button variant='danger' onClick={() => onBlock(bed.roomId)}>
              Block room
            </Button>
          )}
        </DrawerActions>
      </DrawerPanel>
    </>
  )
}

export const BedManagement = () => {
  const [params] = useState({ page: 1, limit: 99 })
  const [openCamp, setOpenCamp] = useState<number | null>(null)
  const [editCamp, setEditCamp] = useState<IGetAllCampsSingleResponse | null>(null)
  const [selectedBed, setSelectedBed] = useState<SelectedBed | null>(null)
  const [extendId, setExtendId] = useState<number | null>(null)
  const dispatch = useDispatch<AppDispatch>()
  const { camps, bedsHistory } = useSelector((store: RootState) => store.accomodation)

  useQueryPagination({
    fetchFunctions: [getCamps],
    params,
    cleanUpFunctions: [clearAccommodationInfo],
  })

  const refetchBoard = () => {
    dispatch(getCamps({ page: 1, limit: 99 }))
    dispatch(getBedsHistory({ page: 1, limit: 999 }))
  }

  useEffect(() => {
    dispatch(getBedsHistory({ page: 1, limit: 999 }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const occupantByBed = useMemo(() => buildOccupantMap(bedsHistory?.data || []), [bedsHistory])
  const campsData = camps?.data || []
  const computed = useMemo(() => computeCamps(campsData, occupantByBed), [campsData, occupantByBed])

  const closeDrawer = () => setSelectedBed(null)

  const handleCheckOut = (assignmentId: number) => {
    closeDrawer()
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(checkOut(assignmentId))
          refetchBoard()
        },
        setParams: { type: ModalTypes.BeforeSabe },
        word: 'check out this person',
      }),
    )
  }

  const handleMarkClean = (bedId: number) => {
    closeDrawer()
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(markBedClean(bedId))
          refetchBoard()
        },
        setParams: { type: ModalTypes.BeforeSabe },
        word: 'mark this bed as clean',
      }),
    )
  }

  const handleReturnToService = (bed: SelectedBed) => {
    closeDrawer()
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(bed.outOfService ? markRoomClean(bed.roomId) : markBedClean(bed.bedId))
          refetchBoard()
        },
        setParams: { type: ModalTypes.BeforeSabe },
        word: 'return this bed to service',
      }),
    )
  }

  const handleBlock = (roomId: number) => {
    closeDrawer()
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(blockRoom(roomId))
          refetchBoard()
        },
        setParams: { type: ModalTypes.BeforeSabe },
        word: 'block this room',
      }),
    )
  }

  const handleExtend = (assignmentId: number) => {
    closeDrawer()
    setExtendId(assignmentId)
  }

  return (
    <>
      <SectionLabel>
        Camps <SectionCount>{computed.length}</SectionCount>
      </SectionLabel>
      <CampList>
        {computed.length ? (
          computed.map((camp) => (
            <CampGroup
              key={camp.id}
              camp={camp}
              active={openCamp === camp.id}
              onToggle={() => setOpenCamp((prev) => (prev === camp.id ? null : camp.id))}
              onEdit={() => setEditCamp(campsData.find((c) => c.id === camp.id) || null)}
              onSelectBed={setSelectedBed}
            />
          ))
        ) : (
          <NoData />
        )}
      </CampList>
      {selectedBed && (
        <BedDrawer
          bed={selectedBed}
          onClose={closeDrawer}
          onCheckOut={handleCheckOut}
          onExtend={handleExtend}
          onMarkClean={handleMarkClean}
          onReturnToService={handleReturnToService}
          onBlock={handleBlock}
        />
      )}
      {extendId !== null && (
        <ExtendStayModal
          id={extendId}
          closeModal={() => {
            setExtendId(null)
            refetchBoard()
          }}
        />
      )}
      {editCamp && <AddAccommodationModal editCamp={editCamp} closeModal={() => setEditCamp(null)} />}
    </>
  )
}
